import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import quizzes from "../data/quizzes";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import { saveTestResult } from "./firestoreUtils";
import Button from "../components/ui/Button";
import Card from "../components/ui/Card";
import { theme } from "../styles/theme";

function Quiz() {
  const navigate = useNavigate();
  const { quizKey } = useParams();

  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [score, setScore] = useState(0);
  const [showResults, setShowResults] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [timeLeft, setTimeLeft] = useState(60 * 60); // 60 minutos em segundos
  const [userAnswers, setUserAnswers] = useState({});
  const [showReview, setShowReview] = useState(false);
  const [reviewQuestion, setReviewQuestion] = useState(0);

  const shuffleArray = (array) => [...array].sort(() => Math.random() - 0.5);

  useEffect(() => {
    const originalQuestions = quizzes[quizKey] || [];
    if (originalQuestions.length > 0) {
      const shuffledQuestions = shuffleArray(originalQuestions);
      setQuestions(shuffledQuestions);
    }
  }, [quizKey]);

  // Timer effect
  useEffect(() => {
    if (timeLeft > 0 && !showResults) {
      const timer = setInterval(() => {
        setTimeLeft((prevTime) => {
          if (prevTime <= 1) {
            // Tempo acabou, finalizar teste
            finishTest();
            return 0;
          }
          return prevTime - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [timeLeft, showResults]);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleAnswer = (option) => {
    const isCorrect = option === questions[currentQuestion]?.correctAnswer;
    setSelectedOption(option);
    setFeedback(isCorrect ? "correct" : "incorrect");

    // Salvar resposta do usuário
    setUserAnswers(prev => ({
      ...prev,
      [currentQuestion]: {
        selected: option,
        correct: isCorrect,
        correctAnswer: questions[currentQuestion]?.correctAnswer
      }
    }));

    if (isCorrect) setScore((prevScore) => prevScore + 1);

    setTimeout(() => {
      if (currentQuestion < questions.length - 1) {
        setCurrentQuestion((prev) => prev + 1);
        setSelectedOption(null);
        setFeedback(null);
      } else {
        finishTest();
      }
    }, 1500);
  };

  const finishTest = () => {
    setShowResults(true);
    saveResults();
  };

  const saveResults = async () => {
    setIsSaving(true);
    const testId = quizKey;
    const correctAnswers = score;
    const totalQuestions = questions.length;
    const finalScore = Math.round((score / totalQuestions) * 100);

    try {
      const result = await saveTestResult(testId, finalScore, correctAnswers, totalQuestions);
      console.log(result.message);
    } catch (error) {
      console.error("Erro ao salvar o resultado do teste:", error);
    } finally {
      setIsSaving(false);
    }
  };

  if (questions.length === 0) {
    return (
      <div style={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, ${theme.colors.primary}15, ${theme.colors.secondary}15)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: theme.spacing.xl,
        fontFamily: theme.typography.fontFamily.primary,
      }}>
        <Card variant="elevated" padding="xl" style={{ textAlign: "center", maxWidth: "400px" }}>
          <div style={{ marginBottom: theme.spacing.lg }}>
            <span style={{ fontSize: theme.typography.fontSize['4xl'] }}>⚠️</span>
          </div>
          <h2 style={{
            fontSize: theme.typography.fontSize.xl,
            fontWeight: theme.typography.fontWeight.bold,
            color: theme.colors.gray[800],
            marginBottom: theme.spacing.md,
          }}>
            Erro ao carregar o quiz
          </h2>
          <p style={{
            fontSize: theme.typography.fontSize.base,
            color: theme.colors.gray[600],
            marginBottom: theme.spacing.xl,
          }}>
            Certifique-se de que o quiz contém perguntas válidas.
          </p>
          <Button
          onClick={() => navigate("/")}
            variant="primary"
            fullWidth
          >
            Voltar ao Início
          </Button>
        </Card>
      </div>
    );
  }

  const current = questions[currentQuestion];
  const isApproved = score >= Math.ceil(questions.length * 0.72);
  const progressPercentage = ((currentQuestion + 1) / questions.length) * 100;

  // Tela de revisão
  if (showReview) {
    const reviewQ = questions[reviewQuestion];
    const userAnswer = userAnswers[reviewQuestion];

  return (
      <div style={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, ${theme.colors.primary}15, ${theme.colors.secondary}15)`,
        padding: theme.spacing.xl,
        fontFamily: theme.typography.fontFamily.primary,
      }}>
        <div style={{ maxWidth: "800px", margin: "0 auto" }}>
          {/* Header da Revisão */}
          <div style={{
            backgroundColor: theme.colors.white,
            borderRadius: theme.borderRadius.xl,
            padding: theme.spacing.lg,
            marginBottom: theme.spacing.lg,
            boxShadow: theme.shadows.sm,
          }}>
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: theme.spacing.md,
            }}>
              <h2 style={{
                fontSize: theme.typography.fontSize.xl,
                fontWeight: theme.typography.fontWeight.bold,
                color: theme.colors.gray[800],
              }}>
                Revisão das Perguntas
              </h2>
              <span style={{
                fontSize: theme.typography.fontSize.sm,
                color: theme.colors.gray[600],
              }}>
                {reviewQuestion + 1} de {questions.length}
              </span>
            </div>
            <div style={{
              display: "flex",
              gap: window.innerWidth < 768 ? theme.spacing.sm : theme.spacing.md,
              flexWrap: "wrap",
              justifyContent: window.innerWidth < 768 ? "center" : "flex-start",
            }}>
              <Button
                onClick={() => setReviewQuestion(Math.max(0, reviewQuestion - 1))}
                variant="outline"
                size="sm"
                disabled={reviewQuestion === 0}
              >
                ← Anterior
              </Button>
              <Button
                onClick={() => setReviewQuestion(Math.min(questions.length - 1, reviewQuestion + 1))}
                variant="outline"
                size="sm"
                disabled={reviewQuestion === questions.length - 1}
              >
                Próxima →
              </Button>
              <Button
                onClick={() => setShowReview(false)}
                variant="primary"
                size="sm"
              >
                Fechar Revisão
              </Button>
            </div>
          </div>

          {/* Questão da Revisão */}
          <Card variant="elevated" padding="xl">
            {reviewQ.image && (
              <div style={{
                marginBottom: theme.spacing.lg,
                borderRadius: theme.borderRadius.lg,
                overflow: "hidden",
                boxShadow: theme.shadows.md,
              }}>
                <img
                  src={reviewQ.image}
                  alt="Pergunta"
                  style={{
                    width: "100%",
                    height: "auto",
                    display: "block",
                  }}
                />
              </div>
            )}

            <h3 style={{
              fontSize: theme.typography.fontSize.lg,
              fontWeight: theme.typography.fontWeight.bold,
              color: theme.colors.gray[800],
              marginBottom: theme.spacing.xl,
              lineHeight: 1.4,
            }}>
              {reviewQ.question}
            </h3>

            <div style={{
              display: "grid",
              gridTemplateColumns: window.innerWidth < 768 ? "1fr" : "repeat(auto-fit, minmax(280px, 1fr))",
              gap: window.innerWidth < 768 ? theme.spacing.sm : theme.spacing.md,
            }}>
              {reviewQ.options?.map((option, index) => {
                const isUserAnswer = userAnswer?.selected === option;
                const isCorrectAnswer = option === reviewQ.correctAnswer;
                
                let optionStyle = {
                  backgroundColor: theme.colors.white,
                  color: theme.colors.gray[700],
                  border: `2px solid ${theme.colors.gray[200]}`,
                };

                if (isCorrectAnswer) {
                  optionStyle = {
                    backgroundColor: theme.colors.success,
                    color: theme.colors.white,
                    border: `2px solid ${theme.colors.success}`,
                  };
                } else if (isUserAnswer && !isCorrectAnswer) {
                  optionStyle = {
                    backgroundColor: theme.colors.error,
                    color: theme.colors.white,
                    border: `2px solid ${theme.colors.error}`,
                  };
                }

                return (
                  <div
                    key={index}
                    style={{
                      ...optionStyle,
                      borderRadius: theme.borderRadius.lg,
                      padding: theme.spacing.lg,
                      fontSize: theme.typography.fontSize.base,
                      fontWeight: theme.typography.fontWeight.medium,
                      minHeight: "80px",
                      display: "flex",
                      alignItems: "center",
                      position: "relative",
                    }}
                  >
                    <span style={{
                      marginRight: theme.spacing.md,
                      fontSize: theme.typography.fontSize.lg,
                      fontWeight: theme.typography.fontWeight.bold,
                      opacity: 0.7,
                    }}>
                      {String.fromCharCode(65 + index)}.
                    </span>
                    {option}
                    
                    {isCorrectAnswer && (
                      <span style={{
                        position: "absolute",
                        right: theme.spacing.md,
                        top: "50%",
                        transform: "translateY(-50%)",
                        fontSize: theme.typography.fontSize.xl,
                      }}>
                        ✅
                      </span>
                    )}
                    {isUserAnswer && !isCorrectAnswer && (
                      <span style={{
                        position: "absolute",
                        right: theme.spacing.md,
                        top: "50%",
                        transform: "translateY(-50%)",
                        fontSize: theme.typography.fontSize.xl,
                      }}>
                        ❌
                      </span>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Status da Questão */}
            <div style={{
              marginTop: theme.spacing.lg,
              padding: theme.spacing.md,
              borderRadius: theme.borderRadius.md,
              backgroundColor: userAnswer?.correct ? `${theme.colors.success}10` : `${theme.colors.error}10`,
              border: `1px solid ${userAnswer?.correct ? theme.colors.success : theme.colors.error}30`,
            }}>
              <p style={{
                fontSize: theme.typography.fontSize.sm,
                color: userAnswer?.correct ? theme.colors.success : theme.colors.error,
                fontWeight: theme.typography.fontWeight.medium,
              }}>
                {userAnswer?.correct ? "✅ Resposta correta!" : "❌ Resposta incorreta"}
              </p>
              {!userAnswer?.correct && (
                <p style={{
                  fontSize: theme.typography.fontSize.sm,
                  color: theme.colors.gray[600],
                  marginTop: theme.spacing.xs,
                }}>
                  Resposta correta: <strong>{userAnswer?.correctAnswer}</strong>
                </p>
              )}
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: "100vh",
      background: `linear-gradient(135deg, ${theme.colors.primary}15, ${theme.colors.secondary}15)`,
      padding: theme.spacing.xl,
      fontFamily: theme.typography.fontFamily.primary,
    }}>
      <div style={{
        maxWidth: "800px",
        margin: "0 auto",
      }}>
      {showResults ? (
          // Results Screen
          <Card variant="elevated" padding="xl" style={{ textAlign: "center" }}>
            <div style={{ marginBottom: theme.spacing.xl }}>
              <span style={{ 
                fontSize: theme.typography.fontSize['4xl'],
                display: "block",
                marginBottom: theme.spacing.md,
              }}>
                {isApproved ? "🎉" : "📝"}
              </span>
              <h1 style={{
                fontSize: theme.typography.fontSize['2xl'],
                fontWeight: theme.typography.fontWeight.bold,
                color: theme.colors.gray[800],
                marginBottom: theme.spacing.md,
              }}>
                Teste Concluído!
              </h1>
            </div>

            {/* Progress Circle */}
            <div style={{ 
              width: "150px", 
              margin: `${theme.spacing.xl} auto`,
            }}>
            <CircularProgressbar
              value={(score / questions.length) * 100}
              text={`${Math.round((score / questions.length) * 100)}%`}
              styles={buildStyles({
                  textColor: isApproved ? theme.colors.success : theme.colors.error,
                  pathColor: isApproved ? theme.colors.success : theme.colors.error,
                  trailColor: theme.colors.gray[200],
                  textSize: "24px",
              })}
            />
          </div>

            {/* Result Status */}
            <div style={{
              backgroundColor: isApproved ? `${theme.colors.success}10` : `${theme.colors.error}10`,
              border: `1px solid ${isApproved ? theme.colors.success : theme.colors.error}30`,
              borderRadius: theme.borderRadius.lg,
              padding: theme.spacing.lg,
              marginBottom: theme.spacing.xl,
            }}>
              <p style={{
                fontSize: theme.typography.fontSize.xl,
                color: isApproved ? theme.colors.success : theme.colors.error,
                fontWeight: theme.typography.fontWeight.bold,
                marginBottom: theme.spacing.sm,
              }}>
            {isApproved ? "Aprovado!" : "Reprovado!"}
          </p>
              <p style={{
                fontSize: theme.typography.fontSize.lg,
                color: theme.colors.gray[600],
              }}>
            Sua pontuação: <strong>{score}</strong> de {questions.length}
          </p>
            </div>

            {isSaving && (
              <div style={{
                backgroundColor: `${theme.colors.info}10`,
                border: `1px solid ${theme.colors.info}30`,
                borderRadius: theme.borderRadius.md,
                padding: theme.spacing.md,
                marginBottom: theme.spacing.lg,
                color: theme.colors.info,
                fontSize: theme.typography.fontSize.sm,
              }}>
                💾 Salvando os resultados...
              </div>
            )}

            {/* Action Buttons */}
            <div style={{
              display: "flex",
              gap: window.innerWidth < 768 ? theme.spacing.sm : theme.spacing.md,
              justifyContent: "center",
              flexWrap: "wrap",
              flexDirection: window.innerWidth < 768 ? "column" : "row",
            }}>
              <Button
                onClick={() => setShowReview(true)}
                variant="outline"
                size="lg"
              >
                📖 Revisar Perguntas
              </Button>
              <Button
            onClick={() => navigate("/")}
                variant="primary"
                size="lg"
              >
                Voltar ao Início
              </Button>
        </div>
          </Card>
      ) : (
          // Quiz Screen
        <div>
            {/* Timer and Progress Bar */}
            <div style={{
              backgroundColor: theme.colors.white,
              borderRadius: theme.borderRadius.xl,
              padding: theme.spacing.lg,
              marginBottom: theme.spacing.lg,
              boxShadow: theme.shadows.sm,
            }}>
              <div style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: theme.spacing.md,
              }}>
                <span style={{
                  fontSize: theme.typography.fontSize.sm,
                  color: theme.colors.gray[600],
                  fontWeight: theme.typography.fontWeight.medium,
                }}>
                  Questão {currentQuestion + 1} de {questions.length}
                </span>
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  gap: theme.spacing.md,
                }}>
                  <span style={{
                    fontSize: theme.typography.fontSize.sm,
                    color: theme.colors.primary,
                    fontWeight: theme.typography.fontWeight.bold,
                  }}>
                    {Math.round(progressPercentage)}%
                  </span>
                  <div style={{
                    backgroundColor: timeLeft <= 300 ? theme.colors.error : theme.colors.primary,
                    color: theme.colors.white,
                    padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                    borderRadius: theme.borderRadius.md,
                    fontSize: theme.typography.fontSize.sm,
                    fontWeight: theme.typography.fontWeight.bold,
                    minWidth: "60px",
                    textAlign: "center",
                  }}>
                    {formatTime(timeLeft)}
                  </div>
                </div>
              </div>
              <div style={{
                width: "100%",
                height: "8px",
                backgroundColor: theme.colors.gray[200],
                borderRadius: theme.borderRadius.full,
                overflow: "hidden",
              }}>
                <div style={{
                  width: `${progressPercentage}%`,
              height: "100%",
                  backgroundColor: theme.colors.primary,
                  borderRadius: theme.borderRadius.full,
                  transition: "width 0.3s ease-in-out",
                }} />
              </div>
          </div>

            {/* Question Card */}
            <Card variant="elevated" padding="xl" style={{ marginBottom: theme.spacing.lg }}>
              {/* Question Image */}
          {current.image && (
                <div style={{
                  marginBottom: theme.spacing.lg,
                  borderRadius: theme.borderRadius.lg,
                  overflow: "hidden",
                  boxShadow: theme.shadows.md,
                }}>
            <img
              src={current.image}
              alt="Pergunta"
              style={{
                width: "100%",
                      height: "auto",
                      display: "block",
                    }}
                  />
                </div>
              )}

              {/* Question Text */}
              <h2 style={{
                fontSize: theme.typography.fontSize.xl,
                fontWeight: theme.typography.fontWeight.bold,
                color: theme.colors.gray[800],
                marginBottom: theme.spacing.xl,
                lineHeight: 1.4,
              }}>
            {current.question}
          </h2>

              {/* Options Grid */}
              <div style={{
              display: "grid",
                gridTemplateColumns: window.innerWidth < 768 ? "1fr" : "repeat(auto-fit, minmax(280px, 1fr))",
                gap: window.innerWidth < 768 ? theme.spacing.sm : theme.spacing.md,
              }}>
                {current.options?.map((option, index) => {
                  const isSelected = selectedOption === option;
                  const isCorrect = option === current.correctAnswer;
                  
                  let optionStyle = {
                    backgroundColor: theme.colors.white,
                    color: theme.colors.gray[700],
                    border: `2px solid ${theme.colors.gray[200]}`,
                  };

                  if (isSelected) {
                    if (feedback === "correct") {
                      optionStyle = {
                        backgroundColor: theme.colors.success,
                        color: theme.colors.white,
                        border: `2px solid ${theme.colors.success}`,
                      };
                    } else {
                      optionStyle = {
                        backgroundColor: theme.colors.error,
                        color: theme.colors.white,
                        border: `2px solid ${theme.colors.error}`,
                      };
                    }
                  }

                  return (
              <button
                key={index}
                onClick={() => handleAnswer(option)}
                disabled={selectedOption !== null}
                style={{
                        ...optionStyle,
                        borderRadius: theme.borderRadius.lg,
                        padding: theme.spacing.lg,
                        fontSize: theme.typography.fontSize.base,
                        fontWeight: theme.typography.fontWeight.medium,
                  cursor: selectedOption === null ? "pointer" : "not-allowed",
                        transition: theme.transitions.normal,
                        textAlign: "left",
                        minHeight: "80px",
                  display: "flex",
                  alignItems: "center",
                        boxShadow: theme.shadows.sm,
                        position: "relative",
                        overflow: "hidden",
                      }}
                      onMouseEnter={(e) => {
                        if (selectedOption === null) {
                          e.target.style.transform = "translateY(-2px)";
                          e.target.style.boxShadow = theme.shadows.md;
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedOption === null) {
                          e.target.style.transform = "translateY(0)";
                          e.target.style.boxShadow = theme.shadows.sm;
                        }
                      }}
                    >
                      <span style={{
                        marginRight: theme.spacing.md,
                        fontSize: theme.typography.fontSize.lg,
                        fontWeight: theme.typography.fontWeight.bold,
                        opacity: 0.7,
                      }}>
                        {String.fromCharCode(65 + index)}.
                      </span>
                {option}
                      
                      {/* Feedback Icon */}
                      {isSelected && (
                        <span style={{
                          position: "absolute",
                          right: theme.spacing.md,
                          top: "50%",
                          transform: "translateY(-50%)",
                          fontSize: theme.typography.fontSize.xl,
                        }}>
                          {feedback === "correct" ? "✅" : "❌"}
                        </span>
                      )}
              </button>
                  );
                })}
              </div>
            </Card>

            {/* Score Display and Finish Button */}
            <div style={{
              backgroundColor: theme.colors.white,
              borderRadius: theme.borderRadius.lg,
              padding: theme.spacing.lg,
              boxShadow: theme.shadows.sm,
            }}>
              <div style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: window.innerWidth < 768 ? theme.spacing.sm : theme.spacing.md,
                flexDirection: window.innerWidth < 768 ? "column" : "row",
              }}>
                <div style={{
                  display: "flex",
                  gap: window.innerWidth < 768 ? theme.spacing.md : theme.spacing.xl,
                  flexDirection: window.innerWidth < 768 ? "column" : "row",
                  alignItems: window.innerWidth < 768 ? "center" : "flex-start",
                }}>
                  <div>
                    <span style={{
                      fontSize: theme.typography.fontSize.sm,
                      color: theme.colors.gray[600],
                    }}>
                      Acertos
                    </span>
                    <div style={{
                      fontSize: theme.typography.fontSize.xl,
                      fontWeight: theme.typography.fontWeight.bold,
                      color: theme.colors.success,
                    }}>
                      {score}
                    </div>
                  </div>
                  <div style={{
                    width: "1px",
                    height: "40px",
                    backgroundColor: theme.colors.gray[200],
                  }} />
                  <div>
                    <span style={{
                      fontSize: theme.typography.fontSize.sm,
                      color: theme.colors.gray[600],
                    }}>
                      Restantes
                    </span>
                    <div style={{
                      fontSize: theme.typography.fontSize.xl,
                      fontWeight: theme.typography.fontWeight.bold,
                      color: theme.colors.gray[700],
                    }}>
                      {questions.length - currentQuestion - 1}
                    </div>
                  </div>
                </div>
                
                <Button
                  onClick={finishTest}
                  variant="primary"
                  size="md"
                >
                  🏁 Terminar Teste
                </Button>
              </div>
            </div>
          </div>
        )}
        </div>
    </div>
  );
}

export default Quiz;